import React from 'react';
import { Link } from 'react-router-dom';
import '../style.css';

// Refactored from 24 hardcoded <article> blocks into one data array + a
// single render path. This is what lets every card carry a "Find a
// therapist" link reliably — with 24 separate hand-written blocks it was too
// easy to add the link to some and miss others.
//
// category maps each condition to the closest of the 14 canonical
// specialisation categories (the same list used by doctors.jsx's filters and
// the matching quiz) so the link actually pre-selects a real, working filter
// rather than a made-up label. Some conditions don't have a clean match —
// those get category: null and a generic "browse all therapists" link
// instead of a false claim of a specific match. A few mappings below are
// reasonable-but-imperfect (e.g. OCD -> Anxiety, Grief -> Career & Life
// Transitions as the closest existing "life change" bucket) — worth a
// second look if any of these don't sit right.
const SERVICES = [
  {
    title: 'Depression',
    img: '/services img/GettyImages-1310564713-1.webp',
    alt: 'Depression therapy',
    category: 'Depression',
    body: `Depression isn't just feeling sad — it's waking up and feeling nothing at all, losing interest in
      things that once made life feel worth living. It can make even the smallest tasks feel exhausting,
      and it has a way of convincing you that things have always been this way and always will be. They
      haven't, and they don't have to stay that way. In our sessions, we work gently to understand what's
      underneath — not just the symptoms, but you as a whole person — and build a path forward that actually
      fits your life.`
  },
  {
    title: 'Anxiety',
    img: '/services img/shutterstock_1531258040-1024x683.jpg',
    alt: 'Anxiety therapy',
    category: 'Anxiety',
    body: `Anxiety is exhausting in a way that's hard to explain to people who haven't lived it. Your mind is
      always ten steps ahead, planning for what could go wrong, replaying conversations, and questioning
      decisions you made weeks ago. Your body keeps score too — the tight chest, the restless nights, the
      feeling that something bad is always just around the corner. Therapy helps you understand where this
      comes from, interrupt the cycle, and start trusting yourself again. You don't have to stay stuck in
      survival mode.`
  },
  {
    title: 'Addiction',
    img: '/services img/hangover-concept-illustration_114360-9530.avif',
    alt: 'Addiction recovery',
    category: 'Addiction & Recovery',
    body: `Addiction isn't a moral failure or a lack of willpower — it's what happens when a person finds
      something that relieves pain, stress, or emptiness, and the brain learns to hold on tight. Recovery
      isn't about white-knuckling through cravings or being lectured about your choices. It's about
      understanding what the substance or behavior is doing for you, addressing that at its root, and
      building a life that doesn't constantly make you want to escape. We approach this work with no
      judgment — only curiosity and care.`
  },
  {
    title: 'OCD',
    img: '/services img/OCD.webp',
    alt: 'OCD therapy',
    category: 'Anxiety',
    linkLabel: 'Find therapists who work with anxiety-related concerns',
    body: `OCD is often misunderstood as just being tidy or particular. In reality, it's an exhausting loop
      of intrusive thoughts that feel threatening and rituals that temporarily quiet them — only for the
      cycle to start all over again. It can take over hours of your day, and the shame of it often keeps
      people from talking about it. Therapy for OCD is highly effective, and it works by helping you
      change your relationship with those thoughts rather than fighting them. You can get your life back
      from this.`
  },
  {
    title: 'Couples Therapy',
    img: '/services img/Woman-and-man-with-a-therapist-1024x683.jpg',
    alt: 'Couples therapy',
    category: 'Relationships',
    body: `Every relationship hits rough patches — periods where communication breaks down, small resentments
      pile up, or you and your partner feel more like roommates than partners. Sometimes it's one big
      event that shook the foundation; sometimes it's years of little things that were never really said.
      Couples therapy isn't about deciding who's right — it's about helping both of you feel genuinely
      heard, understanding each other's patterns, and finding your way back to each other. It takes
      courage to show up for this work, and it matters.`
  },
  {
    title: 'Trauma',
    img: '/services img/little-t-trauma-scaled-e1655326016336.jpg',
    alt: 'Trauma therapy',
    category: 'Trauma & PTSD',
    body: `Trauma has a way of staying in the body long after the event is over. You might find yourself
      overreacting to things that seem small, shutting down when you least expect it, or carrying a
      heaviness you can't quite name. Trauma isn't always dramatic — it can come from childhood
      experiences, difficult relationships, loss, or anything that happened faster than you could
      process it. Healing doesn't mean forgetting. It means the memory no longer has the same grip on
      your present life. We work at your pace, always.`
  },
  {
    title: 'ADHD',
    img: '/services img/self-test-adult-ADHD-screening.webp',
    alt: 'ADHD therapy',
    category: 'Neurodevelopmental',
    body: `Living with ADHD often means spending years feeling like you're trying harder than everyone else
      just to keep up — and still falling short. The missed deadlines, the forgotten conversations, the
      ideas that scatter before you can catch them. It can quietly chip away at your self-esteem in ways
      that medication alone doesn't fix. Therapy helps you understand how your brain works — not as a
      defect, but as a different operating system — and build real strategies that fit you, not a
      one-size-fits-all checklist.`
  },
  {
    title: 'Grief',
    img: '/services img/The-Grieving-Process.jpg',
    alt: 'Grief counselling',
    category: 'Career & Life Transitions',
    linkLabel: 'Find therapists who work with major life changes',
    body: `Grief doesn't follow a schedule or a checklist of stages. It shows up in waves — sometimes
      expected, often when you least expect it, like hearing a song or catching a smell. And it isn't
      only about death; grief can follow the end of a relationship, a job, a version of yourself you
      thought you'd always be. You don't need to get over it. You need space to move through it, at
      your own pace, with someone who won't rush you or tell you how you should be feeling. That's
      what we're here for.`
  },
  {
    title: 'Bipolar Disorder',
    img: '/services img/bipolar-symptoms-assessment-promo-1440x810.jpg',
    alt: 'Bipolar disorder therapy',
    category: null,
    body: `Bipolar disorder can feel like living in two different worlds — the highs that feel electric and
      boundless, and the lows that make it hard to get out of bed. What makes it especially difficult
      is that the shifts can be unpredictable, and the aftermath of a manic episode can leave you
      picking up pieces. Therapy doesn't replace medication when it's needed, but it does something
      medication can't — it helps you understand your patterns, build stability into your daily life,
      and feel less at the mercy of your own mind. You deserve consistent support, not just crisis
      management.`
  },
  {
    title: 'Anger Management',
    img: '/services img/anger.png',
    alt: 'Anger management therapy',
    category: 'Behavioural Therapy',
    body: `Anger itself isn't the problem — it's a completely valid emotion, and often a signal that
      something important to you has been crossed. The problem is when it comes out in ways that
      damage your relationships, your reputation, or your own sense of self. If you find yourself
      exploding over things that feel small in hindsight, or carrying a simmering frustration that
      never quite goes away, therapy can help. We look at what's really underneath the anger —
      the hurt, the fear, the exhaustion — and work on responding rather than reacting. You don't
      have to keep apologising for who you become in those moments.`
  },
  {
    title: 'Relationship & Dating Issues',
    img: '/services img/relation.png',
    alt: 'Relationship and dating therapy',
    category: 'Relationships',
    body: `Whether you keep attracting the wrong people, find yourself repeating the same patterns
      across relationships, or struggle to open up and let someone in — these things rarely fix
      themselves with time alone. Sometimes it's about attachment styles formed long before you
      started dating. Sometimes it's unhealed hurt from a past relationship that's quietly
      running the show. Therapy creates a space to understand your own patterns without judgment,
      so you can start making choices that actually reflect what you want — not just what feels
      familiar. Healthy love is possible, and it starts with understanding yourself first.`
  },
  {
    title: 'Self-Esteem & Confidence',
    img: '/services img/confidence.png',
    alt: 'Self-esteem and confidence therapy',
    category: 'Self-Esteem & Life Adjustment',
    body: `Low self-esteem is quiet and persistent. It's the voice that tells you you're not smart
      enough, not interesting enough, not worth taking up space. It shows up in how you shrink
      in conversations, how quickly you dismiss your own achievements, and how harshly you judge
      yourself for mistakes that you'd forgive in anyone else. The hard truth is that confidence
      isn't something you either have or don't — it's built slowly, through understanding where
      those beliefs came from and learning to challenge them. We work on this together, without
      toxic positivity or empty affirmations. Just honest, steady progress.`
  },
  {
    title: 'Stress & Burnout',
    img: '/services img/burnout.png',
    alt: 'Stress and burnout therapy',
    category: 'Stress & Burnout',
    body: `Burnout doesn't happen overnight. It builds slowly — through months of pushing through,
      telling yourself you'll rest later, taking on more than you should because saying no felt
      impossible. By the time most people seek help, they're running on empty and wondering why
      they can't just push through the way they used to. The answer is that your mind and body
      have limits, and they've been telling you for a while. Therapy for stress and burnout isn't
      just about relaxation techniques — it's about understanding why you got here, rebuilding
      boundaries, and reconnecting with a version of your life that doesn't require you to
      constantly survive it.`
  },
  {
    title: 'Sleep Issues',
    img: '/services img/sleep.png',
    alt: 'Sleep issues therapy',
    category: 'Stress & Burnout',
    linkLabel: 'Find therapists who work with stress-related concerns',
    body: `There's a particular kind of suffering that comes with not being able to sleep. You're tired
      all day, but the moment your head hits the pillow, your mind switches on — replaying
      conversations, rehearsing tomorrow's problems, or simply refusing to quiet down for no reason
      you can name. Chronic sleep issues aren't just about being tired; they affect your mood, your
      relationships, your ability to think clearly. We work on understanding what's driving the
      sleeplessness — whether it's anxiety, unprocessed stress, or ingrained patterns — and building
      the conditions for actual rest. You deserve more than another night staring at the ceiling.`
  },
  {
    title: 'Loneliness & Social Isolation',
    img: '/services img/isolation.png',
    alt: 'Loneliness and social isolation therapy',
    category: null,
    body: `Loneliness is one of the most painful human experiences, and also one of the least talked
      about — because admitting it feels like admitting something is wrong with you. It isn't. The
      years since the pandemic reshaped how people connect, and many people found themselves more
      isolated than they ever expected to be — even in a room full of people. Therapy for loneliness
      isn't about being taught how to make friends. It's about understanding what gets in the way of
      real connection for you — the fear, the past hurt, the walls you built for good reason — and
      gently, slowly, learning to let people in again.`
  },
  {
    title: 'Life Transitions',
    img: '/services img/transitions.png',
    alt: 'Life transitions counselling',
    category: 'Career & Life Transitions',
    body: `Some of the hardest moments in life aren't tragedies — they're transitions. Losing a job,
      ending a marriage, moving to a new city, becoming a parent for the first time. Even the
      changes you chose can leave you feeling unmoored, grieving something you can't quite name,
      or questioning who you are outside of the role you just left behind. Therapy during
      transitions isn't about fixing something broken — it's about having a steady presence
      alongside you while the ground shifts, so you can find your footing and figure out what
      comes next on your own terms.`
  },
  {
    title: 'Perfectionism',
    img: '/services img/perfectionist.png',
    alt: 'Perfectionism therapy',
    category: 'Self-Esteem & Life Adjustment',
    body: `Perfectionism looks like a strength from the outside — and it can be, for a while. But
      underneath it is usually a deep fear: that if you slip up, if you're not excellent, if you
      let people see the unfinished version of you, something terrible will follow. It keeps you
      procrastinating, overworking, never quite satisfied, and unable to celebrate anything you
      achieve because you're already focused on what wasn't good enough. Therapy helps you
      understand where this standard came from, who set it, and whether it's actually serving you
      — or just exhausting you. Good enough, done well, is worth something. You're allowed to
      believe that.`
  },
  {
    title: 'Chronic Illness Support',
    img: '/services img/illness.png',
    alt: 'Chronic illness support therapy',
    category: 'Rehabilitation & Disability',
    body: `Living with a long-term physical condition takes a toll that doctors don't always have time
      to address. There's the grief of the life you had before, the frustration of a body that
      doesn't cooperate, the anxiety of not knowing what tomorrow will feel like, and the
      loneliness of dealing with something invisible to everyone around you. Mental health support
      for chronic illness isn't about staying positive or pushing through — it's about having
      somewhere to put all of it honestly, without being told to look on the bright side. You
      deserve care for the emotional weight of this, not just the physical.`
  },
  {
    title: 'Parenting Support',
    img: '/services img/parent.jpg',
    alt: 'Parenting support therapy',
    category: 'Family & Parenting',
    body: `Parenting is one of the most meaningful things a person can do — and one of the most
      relentlessly demanding. Nobody gives you a manual, and somehow you're expected to manage
      your child's emotional world while still navigating your own. Whether you're struggling
      with a teenager who's shut you out, a toddler who's pushing every boundary, the guilt of
      feeling like you're not doing enough, or simply the loneliness of doing this mostly alone
      — that's worth talking about. Good parents struggle too. Asking for support doesn't mean
      you're failing; it means you care enough to want to do better.`
  },
  {
    title: 'Family Conflict',
    img: '/services img/family.png',
    alt: 'Family conflict therapy',
    category: 'Family & Parenting',
    body: `Family relationships can be the most loving and the most complicated things in a person's
      life — sometimes at the same time. Whether it's a parent who never really understood you,
      sibling dynamics that have never been resolved, or the slow suffocation of a home where
      everything is fine on the surface but never actually fine — these things shape us more
      than almost anything else. Therapy for family conflict isn't about taking sides or assigning
      blame. It's about helping you understand the patterns, decide what you want those
      relationships to look like, and find a way to protect your own peace — whether that means
      repairing things or creating distance.`
  },
  {
    title: 'Premarital Counselling',
    img: '/services img/married.jpg',
    alt: 'Premarital counselling',
    category: 'Relationships',
    body: `The conversations that matter most in a marriage are often the ones couples never have
      before the wedding — about money, about children, about how you each handle conflict,
      about what you need when you're struggling and what you do when things get hard. Premarital
      counselling isn't for couples in crisis; it's for couples who want to start with a strong
      foundation. It's a chance to understand each other more deeply, surface the assumptions
      you've both been carrying, and build the communication habits that will carry you through
      the years ahead. Starting your marriage this way isn't a sign of doubt — it's a sign of
      intention.`
  },
  {
    title: 'Student Mental Health',
    img: '/services img/student.png',
    alt: 'Student mental health therapy',
    category: 'Academic & Exam Pressure',
    body: `Being a student in India today carries a pressure that's hard to overstate. The weight of
      board exams, entrance tests, family expectations, and the constant comparison — all while
      you're still figuring out who you are. For students living away from home, there's also
      the loneliness of hostel life, the pressure to look like you're doing fine, and no one
      nearby who really knows you. You don't have to wait until things fall apart to talk to
      someone. Therapy for students is about building the emotional tools to handle pressure
      without it breaking you — and having a space where you don't have to perform or pretend.`
  },
  {
    title: 'Corporate & Workplace Stress',
    img: '/services img/corporate.png',
    alt: 'Corporate and workplace stress therapy',
    category: 'Career & Life Transitions',
    body: `Work stress has a way of following you home. The difficult manager, the unrealistic
      deadlines, the team politics, the feeling that no matter how much you do it's never quite
      enough — it doesn't stay at the office. Over time it starts affecting your sleep, your
      relationships, and your sense of yourself outside of work. Many people in high-pressure
      careers normalise this level of stress because everyone around them is going through the
      same thing. That doesn't make it healthy. Therapy creates a space to untangle what's
      situational from what's deeper, set real boundaries, and figure out whether this is
      burnout — or something that needs a bigger change.`
  },
  {
    title: "Men's Mental Health",
    img: '/services img/mensmental.png',
    alt: "Men's mental health therapy",
    category: null,
    body: `Most men are taught early that struggling is something to push through, not talk about.
      That asking for help is weakness. That being the provider, the fixer, the one who holds
      things together means keeping your own pain private. The result is that men often suffer
      longer, in silence, before reaching out — and when they do, they sometimes don't quite
      know how to say what they're carrying. This is a space where that's okay. You don't need
      to arrive with everything figured out or explained perfectly. You just need to show up.
      Strength isn't pretending you're fine. It's choosing to deal with what's actually going on.`
  },
];

const Services = () => {
  return (
    <div className="services-page">
      <h2 className="abth">Therapy Services</h2>
      <h5 className="ser2">Find out about the therapy services that we have to offer.</h5>
      <section className="cards">
        <section className="articles">
          {SERVICES.map((service) => (
            <article key={service.title}>
              <div className="article-wrapper">
                <figure>
                  <img src={service.img} alt={service.alt} />
                </figure>
                <div className="article-body">
                  <h2>{service.title}</h2>
                  <p>{service.body}</p>
                  <Link
                    to={
                      service.category
                        ? `/doctors?tab=therapists&focus=${encodeURIComponent(service.category)}`
                        : '/doctors?tab=therapists'
                    }
                    style={{
                      display: 'inline-block', marginTop: 12, fontSize: 14, fontWeight: 600,
                      color: '#e8622a', textDecoration: 'none'
                    }}
                  >
                    {service.linkLabel || `Find a therapist for ${service.title.toLowerCase()}`} →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </section>
      </section>
    </div>
  );
};

export default Services;